import { HttpClient } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { AppService } from "./../../app.service";
import { UserProfileService } from "./../../user-profile/user-profile.service";
import { LoginService } from "./../../login/login.service";
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewEncapsulation,
  OnDestroy,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { ActivatedRoute, Router, NavigationEnd } from "@angular/router";
import { MessageService } from "primeng/api";
import { SharedService } from "../shared.service";
import { Subscription } from "rxjs";
import { PermissionService } from "../permissions/permissions.service";
import { LocationItemService } from "../../sales/location-item/location-item.service";
import { AppUtils } from "../../app.utils";
import { HttpErrorResponse } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";

@Component({
  selector: "ba-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.css"],
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit, OnDestroy {
  isSideNavigationOpen: any;
  currentRoute: any;
  userName: string;
  imageUrl: any;
  crumbs: any = [];
  adminRole: boolean;
  permissions: Array<any> = [];
  userData: Object;
  navSubscription: Subscription;
  userNameSubscription: Subscription;
  profileSubscription: Subscription;
  profileImageSubscription: Subscription;
  resetPasswordModal: boolean;
  loyaltySearch: boolean;
  showFilteredResult: boolean;
  doorScanView = "transaction";
  @ViewChild("searchInput")
  loyaltyNumSearchInputRef: ElementRef<HTMLInputElement>;
  @Output() updateNavigationState = new EventEmitter();
  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public loginService: LoginService,
    public messageService: MessageService,
    public sharedService: SharedService,
    private cookieService: CookieService,
    public appService: AppService,
    public userProfileService: UserProfileService,
    public permissionService: PermissionService,
    public translate: TranslateService,
    public httpClient: HttpClient,
    public locationItemService: LocationItemService,
    public appUtils: AppUtils,
  ) {
    this.adminRole = this.permissionService.isAdmin;
    this.permissions = this.permissionService.getCurrentPermissions();

    document.addEventListener("click", this.offClickHandler.bind(this));
  }

  ngOnInit() {
    if (this.route.firstChild && this.route.firstChild.snapshot) {
      const childSnapshot = this.route.firstChild?.snapshot.children[0];
      const { parentRoute, routePath } = childSnapshot.data;
      this.currentRoute = childSnapshot.data.topHeader;
      this.getBreadCrumbData(this.currentRoute, parentRoute, routePath);
    }
    this.router.events.subscribe((routeObj) => {
      if (routeObj instanceof NavigationEnd) {
        if (this.route.firstChild && this.route.firstChild.snapshot) {
          const childSnapshot = this.route.firstChild?.snapshot.children[0];
          const { parentRoute, routePath } = childSnapshot?.data || {
            parentRoute: "",
            routePath: "",
          };
          this.currentRoute = childSnapshot?.data?.topHeader || "";
          this.getBreadCrumbData(this.currentRoute, parentRoute, routePath);
        } else {
          this.currentRoute = "";
          this.getBreadCrumbData(this.currentRoute);
        }
      }
    });
    this.getSubscriptionData();
    this.getLoggedInUserDetail();
    this.getUserProfileData();

    this.appService.getUpdateLoyaltyNumberSearchInputSubject$?.subscribe(
      (keywords: string) => {
        if (keywords) {
          this.setLoyaltyNumberSearchInput(keywords);
          this.showFilteredResult = true;
        }
      },
    );

    this.appService.visibleLoyaltySearchInputs?.subscribe((res: any) => {
      this.loyaltySearch = res;
    });
    this.appService.doorScanViewSubject$.subscribe((res: any) => {
      if (res) {
        this.doorScanView = res;
        if (this.doorScanView === "item") {
          this.loyaltyNumSearchInputRef.nativeElement.value = "";
          this.showFilteredResult = false;
        }
      }
    });
  }

  ngOnDestroy() {
    this.navSubscription.unsubscribe();
    this.profileImageSubscription.unsubscribe();
    this.profileSubscription.unsubscribe();
  }

  onReloadClick() {
    if (this.doorScanView === "transaction") {
      this.appService.headerReloadClick.next();
    }
  }

  getBreadCrumbData(route: any, parentRoute?: any, routePath?: any) {
    if (parentRoute) {
      this.crumbs = [
        { description: this.translate.instant(parentRoute), url: routePath },
        { description: this.translate.instant(route), url: "" },
      ];
    } else if (route) {
      this.crumbs = [{ description: this.translate.instant(route), url: "" }];
    }
  }

  /**
   * To get subscribed data from components
   */
  getSubscriptionData() {
    this.navSubscription = this.appService.updatedNavigationState$.subscribe(
      (state) => {
        this.isSideNavigationOpen = state;
      },
    );
    this.profileImageSubscription =
      this.appService.updatedUserProfileImage$.subscribe((profileImage) => {
        this.imageUrl = profileImage;
      });
    this.profileSubscription = this.appService.updatedUserData$.subscribe(
      (data) => {
        this.userData = data;
        this.userName = data?.username;
      },
    );
  }

  /**
   * To get logged in user name from local storage
   */
  getLoggedInUserDetail() {
    const user: any = JSON.parse(localStorage.getItem("loggedInUser"));
    this.userName = user && user.username;
  }

  /**
   * To get logged in user profile required for showing profile image in header
   */
  getUserProfileData() {
    if (localStorage.getItem("userProfileData")) {
      const userProfileData = JSON.parse(
        localStorage.getItem("userProfileData"),
      );
      this.userData = userProfileData;
      this.appService.updateUserData(this.userData);
      if (
        userProfileData.hasProfileImage ||
        (userProfileData.hasProfileImage &&
          this.permissions.indexOf("getUserImage") > -1)
      ) {
        this.getProfileImage();
      }
    } else {
      this.userProfileService.getUserProfile().subscribe((response) => {
        this.userData = response;
        this.appService.updateUserData(this.userData);
        if (
          response.hasProfileImage ||
          (response.hasProfileImage &&
            this.permissions.indexOf("getUserImage") > -1)
        ) {
          this.getProfileImage();
        }
      });
    }
  }

  /**
   * To get user profile image through blob
   */
  getProfileImage() {
    this.sharedService.getProfileImageFromBlob().subscribe((blobValue) => {
      this.createImageFromBlob(blobValue);
    });
  }

  /**
   * For creating image from blob
   * @param image Image
   */
  createImageFromBlob(image: Blob) {
    const reader = new FileReader();
    reader.addEventListener(
      "load",
      () => {
        this.imageUrl = reader.result;
        this.appService.updateUserProfileImage(this.imageUrl);
      },
      false,
    );
    if (image) {
      reader.readAsDataURL(image);
    }
  }

  /**
   * openSidebar will open the sidebar on click of mobile menu icon
   */
  openSidebar(): void {
    this.isSideNavigationOpen = !this.isSideNavigationOpen;
    this.updateNavigationState.emit(this.isSideNavigationOpen);
    this.appService.updateWidthForReporting.next(this.isSideNavigationOpen);
  }

  /**
   * openScanner will open the QR code scanner
   */
  openScanner() {
    this.appService.openLoyaltyNumberScanner.next();
  }

  /**
   * offClickHandler will close the sidebar by clicking anywhere in the page
   * @param event Clicked Target
   */
  offClickHandler(event: any) {
    const getWindowWidth = window.innerWidth;
    if (getWindowWidth <= 840) {
      if (!event.target.closest(".mobile-menu, .navigationBar")) {
        this.isSideNavigationOpen = false;
        this.updateNavigationState.emit(this.isSideNavigationOpen);
      }
    }
  }

  /**
   * log out user on logout button press
   */
  logOutUser() {
    localStorage.setItem("globalPresetFlag", JSON.stringify(false));

    const presetData = localStorage.getItem("globalPresetData")
      ? JSON.parse(localStorage.getItem("globalPresetData")!)
      : null;

    if (presetData && (presetData["id"] || presetData["view"])) {
      presetData["columns"] = JSON.stringify(presetData["columns"]);
      this.updateSelectedPreset(presetData);
    }

    this.loginService.logOut(true).subscribe({
      next: () => this.performLogoutCleanup(),

      error: (err: HttpErrorResponse) => {
        if (err.status === 400) {
          // temporary fix for some logout issues when using website in multiple tabs on same browser
          // TODO: need to investigate the reason for 400
          this.performLogoutCleanup();
        } else {
          // don't do anything
        }
      },
    });
  }

  /**
   * clear cache after logout
   */
  performLogoutCleanup() {
    localStorage.clear();
    sessionStorage.clear();
    this.clearAllAccessibleCookies();
    this.appService.changeSwitchUserState();
    this.appService.updateUserProfileImage("");
    this.router.navigate(["login"]);
  }

  /**
   * Deletes all accessible cookies for the current domain.
   * - Iterates over cookies returned by ngx-cookie-service
   * - Deletes each cookie explicitly using path '/'
   * - Safe for logout scenarios
   */
  clearAllAccessibleCookies(): void {
    // Fetch all cookies as key-value object
    const cookies: { [key: string]: string } = this.cookieService.getAll();

    // Exit early if no cookies exist
    if (!cookies || Object.keys(cookies).length === 0) {
      return;
    }

    // Iterate and delete each cookie individually
    Object.keys(cookies).forEach((cookieName: string) => {
      /**
       * Deleting with path '/'
       */
      this.cookieService.delete(cookieName, "/");
    });
  }

  /**
   * updateSelectedPreset will update the preset for all
   */
  updateSelectedPreset(presetDataToSave: any) {
    this.locationItemService
      .updateSelectedPreset(presetDataToSave)
      .subscribe(() => {});
  }

  /**
   * To search loyalty Search
   */
  setLoyaltySearch(data: any) {
    this.appService.loyaltySearchInputs.next(data);
    if (this.doorScanView === "item") {
      this.loyaltyNumSearchInputRef.nativeElement.value = "";
      this.showFilteredResult = false;
    }
  }

  /**
   * fill loyalty number search input from scanned data
   * @param keywords string
   */
  setLoyaltyNumberSearchInput(keywords: string) {
    if (this.loyaltyNumSearchInputRef) {
      this.loyaltyNumSearchInputRef.nativeElement.value = keywords;
    }
  }

  checkSearchResult() {
    this.showFilteredResult = this.appUtils.checkSearchFilterLength(
      this.loyaltyNumSearchInputRef.nativeElement.value,
    );
  }

  clearSearchResult() {
    this.loyaltyNumSearchInputRef.nativeElement.value = "";
    this.showFilteredResult = false;
    this.appService.loyaltySearchInputs.next();
  }
}
